import { useEffect, useState, useRef } from 'react';
import {
  ShieldCheck, BadgeCheck, LockKeyhole, CalendarDays, UserRound, UserRoundCheck,
  UserPlus, Stethoscope, Building2, MessageSquareText, ClipboardPlus, Pill,
  FlaskConical, Flag, CalendarClock, Users, FileText, ScrollText, FolderClosed,
  Clock, CheckCircle2, X, Info, KeyRound, Target, XCircle,
} from 'lucide-react';
import QRCode from 'qrcode';
import BookFollowUpBooking from '@/components/healthfile/BookFollowUpBooking';
import './patient-health-file-founder-replica.css';

/* Founder-reference REPLICA — presentational. Accepts a `data` prop (mapped
   from the backend) and a `shareUrl`. Falls back to hardcoded demo data so
   the /dhf-replica preview route keeps working unchanged. */

const REF_IMG = 'https://customer-assets.emergentagent.com/job_57ca5f65-dd43-4a16-96f2-1c2051c0d172/artifacts/i49o2eec_Screenshot%202026-06-03%20at%2012.00.02%E2%80%AFPM.png';

const DEMO = {
  patient: { name: 'Rohan Sharma', age: '42', gender: 'Male' },
  visit_date: '2 June 2026',
  doctor: { name: 'Dr. Anjali Mehta', specialty: 'General Physician', qualifications: 'MBBS, MD (General Medicine)', registration: 'GJ/2010/12345', signature_url: '' },
  consultation_type: 'In-person',
  clinic_location: 'HeartCare Clinic, Powai',
  reason_for_visit: 'Fever, sore throat, mild cough, and\nbody ache for 3 days.',
  assessment: ['Viral upper respiratory infection.', 'No signs of bacterial infection.', 'Condition is mild and self-limiting.'],
  medicines: [
    { name: 'Paracetamol', strength: '650 mg', dose: '1 tablet', frequency: 'Every 8 hours', duration: '3 days', notes: 'For fever or body ache' },
    { name: 'Cetirizine', strength: '10 mg', dose: '1 tablet', frequency: 'Once daily (night)', duration: '5 days', notes: 'For cold / runny nose' },
    { name: 'Dextromethorphan', strength: '10 mg/5ml', dose: '10 ml', frequency: 'Every 8 hours', duration: '5 days', notes: 'For cough' },
  ],
  tests: ['Complete Blood Count (CBC)', 'C-Reactive Protein (CRP)', 'Rapid Antigen Test (If fever persists > 3 days)'],
  red_flags: ["High fever above 102°F that doesn't reduce with medicine", 'Difficulty breathing or chest pain', 'Persistent vomiting or dehydration'],
  follow_up: 'If symptoms do not improve in 3–5 days or worsen at any time,\nfollow up with Dr. Anjali Mehta.',
  file_id: 'DS-VF-20260602-1A7B',
  created_at_str: '2 June 2026  •  11:35 AM',
};

const TABS = [
  { key: 'summary', label: 'Summary', Icon: ScrollText, cls: 'phf-tab--summary' },
  { key: 'prescriptions', label: 'Prescriptions', Icon: Pill, cls: 'phf-tab--prescriptions', soon: true },
  { key: 'labs', label: 'Lab Reports', Icon: FlaskConical, cls: 'phf-tab--labs', soon: true },
  { key: 'documents', label: 'Documents', Icon: FolderClosed, cls: 'phf-tab--documents', soon: true },
  { key: 'timeline', label: 'Care Timeline', Icon: Clock, cls: 'phf-tab--timeline', soon: true },
];

const LIST_CAP = 8;

export default function PatientHealthFileFounderReplica({ data, shareUrl, doctorSlug, healthFileToken }) {
  const d = data || DEMO;
  const link = shareUrl || 'https://docsite.care/h/sample';
  const [drawerOpen, setDrawerOpen] = useState(() => (typeof window !== 'undefined' ? window.innerWidth > 1180 : true));
  const [qr, setQr] = useState('');
  const [medAll, setMedAll] = useState(false);
  const [testAll, setTestAll] = useState(false);
  const [flagAll, setFlagAll] = useState(false);
  const [bookWith, setBookWith] = useState('current');
  const [consent, setConsent] = useState(true);
  const overlay = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('overlay') : null;

  useEffect(() => {
    QRCode.toDataURL(link, { width: 240, margin: 1, color: { dark: '#07122f', light: '#ffffff' } }).then(setQr).catch(() => {});
  }, [link]);

  // ── Fallbacks (graceful) ────────────────────────────────────────────────
  const ageStr = d.patient?.age ? String(d.patient.age) : 'Age not recorded';
  const ageGender = d.patient?.gender ? `${ageStr} / ${d.patient.gender}` : ageStr;
  const consult = d.consultation_type || 'Consultation';
  const regText = d.doctor?.registration ? `Reg. No. ${d.doctor.registration}` : 'Reg. No. not recorded';
  const assessment = (d.assessment || []).filter(Boolean);
  const medicines = d.medicines || [];
  const rxAttachments = d.rx_attachments || [];
  const threadProgress = d.thread_progress || [];
  const tests = d.tests || [];
  const redFlags = d.red_flags || [];
  // v9 · Visit-level Do's / Don'ts (Simple condition flow). Patient view.
  const dos = (d.dos || []).filter(Boolean);
  const donts = (d.donts || []).filter(Boolean);
  const signatureUrl = d.signature_url || d.doctor?.signature_url || '';
  const sigName = (d.doctor?.name || '').replace(/^Dr\.?\s+/, '') || 'Signed';
  const reason = d.reason_for_visit || 'Not recorded';
  const followUp = d.follow_up || 'Follow up as advised by your doctor';

  const visMeds = medAll ? medicines : medicines.slice(0, LIST_CAP);
  const visTests = testAll ? tests : tests.slice(0, LIST_CAP);
  const visFlags = flagAll ? redFlags : redFlags.slice(0, LIST_CAP);

  return (
    <div className="phf-viewport" data-testid="phf-replica">
      <div className="phf-stage">
        {overlay && <img className="phf-overlay" src={REF_IMG} alt="" style={{ opacity: Number(overlay) || 0.4 }} />}
        {drawerOpen && <div className="phf-scrim" onClick={() => setDrawerOpen(false)} />}
        <div className="phf-folder">
          <div className="phf-tabs">
            {TABS.map(({ key, label, Icon, cls, soon }) => (
              <div
                key={key}
                className={`phf-tab ${cls} ${soon ? 'phf-tab--soon' : 'phf-tab--active'}`}
                data-testid={`phf-tab-${key}`}
                aria-disabled={soon ? 'true' : undefined}
                title={soon ? 'Coming soon with HealthifyX' : 'Summary'}
              >
                <Icon size={15} />
                <span className="phf-tab-label">{label}</span>
                {soon && <span className="phf-tab-soon">Soon</span>}
              </div>
            ))}
          </div>

          <div className="phf-sheet">
            <div className="phf-board">
              {/* ── MAIN CLINICAL COLUMN ──────────────────────────────── */}
              <div className="phf-main">
                <header className="phf-header">
                  <div className="phf-logo">
                    <img src="/docsite-logo.png" alt="DocSite" />
                    <span className="phf-logo-text"><b>DocSite</b><span>FOR DOCTORS</span></span>
                  </div>
                  <div className="phf-hdiv" />
                  <div className="phf-shield"><ShieldCheck size={26} /></div>
                  <div className="phf-htitles">
                    <h1 data-testid="phf-title">DocSite Verified – Patient Digital Health File <i>(Patient Owned)</i></h1>
                    <p>Your consultation summary — medical clinical data created for you during this visit.</p>
                  </div>
                </header>

                <div className="phf-identity">
                  <div className="phf-id-cell phf-id-cell--patient">
                    <span className="phf-id-avatar"><UserRound size={26} color="#37589f" /></span>
                    <div><div className="phf-id-name">{d.patient?.name || 'Patient'}</div><div className="phf-id-sub" data-testid="phf-age-gender">{ageGender}</div></div>
                  </div>
                  <div className="phf-id-cell"><span className="phf-id-ico"><CalendarDays size={18} /></span><div><div className="phf-id-label">Visit Date</div><div className="phf-id-value">{d.visit_date || '—'}</div></div></div>
                  <div className="phf-id-cell"><span className="phf-id-ico"><UserRoundCheck size={18} /></span><div><div className="phf-id-label">Doctor</div><div className="phf-id-value">{d.doctor?.name || '—'}</div></div></div>
                  <div className="phf-id-cell"><span className="phf-id-ico"><Stethoscope size={18} /></span><div><div className="phf-id-label">Specialty</div><div className="phf-id-value">{d.doctor?.specialty || '—'}</div></div></div>
                  <div className="phf-id-cell"><span className="phf-id-ico"><Building2 size={18} /></span><div><div className="phf-id-label">Consultation Type</div><div className="phf-id-value" data-testid="phf-consult-type">{consult}</div></div></div>
                </div>

                <div className="phf-status">
                  <span className="phf-status-label">File Status</span>
                  <span className="phf-chip phf-chip--green"><CheckCircle2 size={16} /> Doctor-created clinical file</span>
                  <span className="phf-chip phf-chip--blue"><BadgeCheck size={16} /> Patient-owned</span>
                  <span className="phf-status-note">Created during a consented clinical visit</span>
                </div>

                <div className="phf-grid">
                  <Tile icon={<MessageSquareText size={20} />} ico="phf-ico-blue" title="Reason for Visit">
                    <ClampText text={reason} lines={6} pre />
                  </Tile>
                  <Tile icon={<ClipboardPlus size={20} />} ico="phf-ico-blue" title="Doctor's Diagnosis" testid="phf-assessment">
                    {assessment.length === 0 ? <p className="phf-tile-p">Not recorded</p>
                      : <ClampText text={assessment.join('\n')} lines={6} pre />}
                  </Tile>

                  <Tile icon={<Pill size={20} />} ico="phf-ico-blue" title="Medicines Prescribed">
                    {medicines.length === 0 ? <p className="phf-tile-p">No medicines prescribed</p> : (
                      <>
                        <div className="phf-medwrap">
                          <table className="phf-medtable" data-testid="phf-medicines-table">
                            <colgroup>
                              <col className="phf-c-med" /><col className="phf-c-str" /><col className="phf-c-dose" />
                              <col className="phf-c-freq" /><col className="phf-c-dur" /><col className="phf-c-notes" />
                            </colgroup>
                            <thead><tr><th>Medicine</th><th>Molecule <span style={{ color: '#dc2626', fontWeight: 800 }}>*</span></th><th>Strength</th><th>Dose</th><th>Frequency</th><th>Duration</th><th>Notes</th></tr></thead>
                            <tbody>
                              {visMeds.map((m, i) => (
                                <tr key={i}>
                                  <td>{m.name || '—'}</td>
                                  <td style={{ fontSize: '0.85em', color: '#475569' }}>
                                    {(m.molecule || m.generic) ? (
                                      <>{m.molecule || m.generic} <span style={{ color: '#dc2626', fontWeight: 800 }}>*</span></>
                                    ) : <em style={{ color: '#94a3b8' }}>—</em>}
                                  </td>
                                  <td>{m.strength || '—'}</td><td>{m.dose || '—'}</td><td>{m.frequency || '—'}</td><td>{m.duration || '—'}</td><td>{m.notes || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {medicines.length > LIST_CAP && (
                          <button className="phf-viewmore" onClick={() => setMedAll(v => !v)}>{medAll ? 'View less' : `+${medicines.length - LIST_CAP} more medicines`}</button>
                        )}
                        <p className="phf-med-note">Take medicines after food. Drink plenty of fluids and rest well.</p>
                        <p className="phf-med-note" style={{ marginTop: 8, padding: '10px 12px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, color: '#7c2d12' }} data-testid="phf-molecule-footnote">
                          <span style={{ color: '#dc2626', fontWeight: 800 }}>*</span> If your pharmacy offers or substitutes a different brand than the one prescribed above, it is your responsibility to ask the pharmacist and personally confirm that the generic molecule(s) shown in the prescription above are correct and that you want the same.
                        </p>
                      </>
                    )}
                  </Tile>

                  {rxAttachments.length > 0 && (
                    <Tile icon={<FileText size={20} />} ico="phf-ico-blue" title="Digital Prescription">
                      <p className="phf-tile-p" style={{ marginBottom: 12 }}>
                        Your doctor has issued a signed digital prescription. Download or print for your pharmacy.
                      </p>
                      {rxAttachments.map((rx) => (
                        <div key={rx.rxId} className="phf-rx-attachment"
                             style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                      gap: 12, padding: '12px 14px', borderRadius: 10,
                                      border: '1px solid #E1EAF6', background: '#FAFBFD', marginBottom: 8 }}
                             data-testid={`phf-rx-${rx.rxId}`}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 700,
                                          color: '#123B91', fontSize: 12, marginBottom: 2 }}>
                              {rx.rxId}
                            </div>
                            <div style={{ fontSize: 12, color: '#6B7A8F' }}>
                              Issued {rx.issuedAt ? new Date(rx.issuedAt).toLocaleDateString('en-IN',
                                { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                            </div>
                          </div>
                          <a href={`${process.env.REACT_APP_BACKEND_URL || ''}${rx.downloadUrl}`}
                             target="_blank" rel="noopener noreferrer"
                             data-testid={`phf-rx-download-${rx.rxId}`}
                             style={{ padding: '8px 14px', background: '#123B91', color: '#fff',
                                      borderRadius: 8, textDecoration: 'none', fontSize: 12,
                                      fontWeight: 700, whiteSpace: 'nowrap' }}>
                            Download PDF
                          </a>
                        </div>
                      ))}
                      <p style={{ fontSize: 11, color: '#6B7A8F', marginTop: 8 }}>
                        Cryptographically verified. Any pharmacist can confirm authenticity by scanning
                        the QR code inside the PDF.
                      </p>
                    </Tile>
                  )}

                  <Tile icon={<FlaskConical size={20} />} ico="phf-ico-green" title="Tests Advised">
                    {tests.length === 0 ? <p className="phf-tile-p">No tests advised</p> : (
                      <>
                        <ul className="phf-checklist">
                          {visTests.map((t, i) => <li key={i}><CheckCircle2 size={18} /> <span className="phf-clamp2">{t}</span></li>)}
                        </ul>
                        {tests.length > LIST_CAP && (
                          <button className="phf-viewmore" onClick={() => setTestAll(v => !v)}>{testAll ? 'View less' : `+${tests.length - LIST_CAP} more`}</button>
                        )}
                      </>
                    )}
                  </Tile>

                  <Tile icon={<Flag size={20} />} ico="phf-ico-red" title="Red Flags" cls="phf-tile--risk">
                    {redFlags.length === 0 ? (
                      <p className="phf-tile-p">No specific red flags recorded. Seek care if you feel worse.</p>
                    ) : (
                      <>
                        <ul className="phf-redlist">{visFlags.map((t, i) => <li key={i}><span className="phf-clamp2">{t}</span></li>)}</ul>
                        {redFlags.length > LIST_CAP && (
                          <button className="phf-viewmore" onClick={() => setFlagAll(v => !v)}>{flagAll ? 'View less' : `+${redFlags.length - LIST_CAP} more`}</button>
                        )}
                        <p className="phf-red-cta">Seek immediate medical attention if any of the above occur.</p>
                      </>
                    )}
                  </Tile>
                  <Tile icon={<CalendarClock size={20} />} ico="phf-ico-blue" title="Follow-up Plan">
                    <ClampText text={followUp} lines={5} pre />
                    <div className="phf-fu-row">
                      <button className="phf-btn-primary" onClick={() => setDrawerOpen(true)} data-testid="phf-book-followup"><CalendarDays size={18} /> Book Follow-up</button>
                      <div className="phf-bubble">This file can travel with your booking.</div>
                    </div>
                  </Tile>

                  {/* v9 · Do's / Don'ts (visit-level). Rendered only when the doctor
                      authored at least one entry — legacy DHFs stay pristine. */}
                  {dos.length > 0 && (
                    <Tile icon={<CheckCircle2 size={20} />} ico="phf-ico-green" title="Do's" testid="phf-dos">
                      <ul className="phf-checklist">
                        {dos.map((t, i) => (
                          <li key={i}><span className="phf-check-ico"><CheckCircle2 size={14}/></span><span>{t}</span></li>
                        ))}
                      </ul>
                    </Tile>
                  )}
                  {donts.length > 0 && (
                    <Tile icon={<XCircle size={20} />} ico="phf-ico-red" title="Don&rsquo;ts" testid="phf-donts" cls="phf-tile--risk">
                      <ul className="phf-checklist">
                        {donts.map((t, i) => (
                          <li key={i}><span className="phf-check-ico" style={{ color: '#b91c1c' }}><XCircle size={14}/></span><span>{t}</span></li>
                        ))}
                      </ul>
                    </Tile>
                  )}
                </div>

                {/* ── Care Progress · doctor-authored milestones for this
                    condition. Rendered ONLY when the doctor has set at
                    least one milestone on any thread this DHF touches.
                    Patient-safe fields only. */}
                {threadProgress.some(t => (t.milestones || []).length > 0) && (
                  <section className="phf-progress" data-testid="phf-progress">
                    <div className="phf-progress-head">
                      <span className="phf-progress-icon"><Target size={19} /></span>
                      <h3>Your care progress</h3>
                    </div>
                    <p className="phf-progress-lede">
                      Your doctor set the goals below for this care plan. Each medicine you're on is aimed at pursuing them.
                    </p>
                    {threadProgress.filter(t => (t.milestones || []).length > 0).map(t => (
                      <div className="phf-progress-thread" key={t.thread_id} data-testid={`phf-progress-thread-${t.thread_id}`}>
                        <div className="phf-progress-thread-head">
                          <span className="phf-progress-thread-name">{t.name}</span>
                          <span className="phf-progress-thread-summary">
                            {t.on_track} of {t.total} goal{t.total === 1 ? '' : 's'} hit
                          </span>
                        </div>
                        <ul className="phf-progress-milestones">
                          {(t.milestones || []).map(ms => {
                            const statusMeta = {
                              hit:       { label: 'Hit',       cls: 'phf-ms-hit',    dot: '✅' },
                              missed:    { label: 'Missed',    cls: 'phf-ms-missed', dot: '⚠️' },
                              cancelled: { label: 'Cancelled', cls: 'phf-ms-cxl',    dot: '—' },
                              open:      { label: 'On track',  cls: 'phf-ms-open',   dot: '⏳' },
                            }[ms.status || 'open'] || { label: 'On track', cls: 'phf-ms-open', dot: '⏳' };
                            const targetStr = ms.target_at ? new Date(ms.target_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '';
                            return (
                              <li className="phf-progress-ms" key={ms.id} data-testid={`phf-progress-ms-${ms.id}`}>
                                <div className="phf-progress-ms-head">
                                  <span className={`phf-ms-status ${statusMeta.cls}`}>{statusMeta.dot} {statusMeta.label}</span>
                                  <span className="phf-progress-ms-label">{ms.label}</span>
                                  {targetStr && <span className="phf-progress-ms-target">target · {targetStr} ({ms.weeks}w)</span>}
                                </div>
                                {(ms.driven_by || []).length > 0 && (
                                  <div className="phf-progress-driven">
                                    <span className="phf-progress-driven-label">Pursued by</span>
                                    {ms.driven_by.map((mn, i) => (
                                      <span className="phf-progress-driven-med" key={i}>💊 {mn}</span>
                                    ))}
                                  </div>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ))}
                    <p className="phf-progress-foot">
                      These goals are what your doctor is aiming for. Please follow the medicine plan and check in with your doctor as scheduled.
                    </p>
                  </section>
                )}

                <section className="phf-provenance" data-testid="phf-provenance">
                  <div className="phf-prov-col">
                    <div className="phf-prov-head"><span className="phf-prov-shield"><ShieldCheck size={19} /></span><h3>Clinical Record Provenance</h3></div>
                    <p className="phf-prov-text">This medical clinical data was created by your doctor for you during this visit, based on your consent.</p>
                    <ul className="phf-prov-bullets">
                      <li><CheckCircle2 size={16} /> Created for your later use and for use by your current or future doctors.</li>
                      <li><CheckCircle2 size={16} /> You control consent for any other doctor to access or use this record.</li>
                      <li><CheckCircle2 size={16} /><span>Locked record after creation.<small>Non-editable by patient and creator doctor.</small></span></li>
                    </ul>
                  </div>
                  <div className="phf-prov-col">
                    <div className="phf-prov-label">Creator Doctor</div>
                    <div className="phf-prov-docname">{d.doctor?.name || '—'}</div>
                    <div className="phf-prov-meta">{d.doctor?.qualifications || 'Qualifications not recorded'}<br />{regText}</div>
                    <div className="phf-prov-note">Creator of this clinical medical data.</div>
                  </div>
                  <div className="phf-prov-col">
                    <div className="phf-prov-label">Clinical Creation Signature</div>
                    {signatureUrl
                      ? <img className="phf-sign-img" src={signatureUrl} alt="Doctor signature" data-testid="phf-signature-img" />
                      : <div className="phf-sign" data-testid="phf-signature-text">{sigName}</div>}
                    <div className="phf-prov-time">{d.created_at_str || '—'}</div>
                  </div>
                  <div className="phf-prov-col phf-prov-col--qr">
                    <div className="phf-prov-label">File ID</div>
                    <div className="phf-prov-fileid" data-testid="phf-file-id">{d.file_id || '—'}</div>
                    {qr && <div className="phf-prov-qrbox"><img src={qr} alt="QR" data-testid="phf-qr" /></div>}
                    <span className="phf-prov-scan">Scan to view this file <Info size={13} /></span>
                  </div>
                </section>

                <div className="phf-trust">
                  <div className="phf-trust-tile"><span className="phf-trust-ico phf-ico-blue"><Users size={20} /></span><div><h4>Patient Consent &amp; Sharing Control</h4><p>Only the doctors may access or use this record only with your consent.</p></div></div>
                  <div className="phf-trust-tile"><span className="phf-trust-ico phf-ico-blue"><LockKeyhole size={20} /></span><div><h4>Record Integrity</h4><p>Locked record after creation. Non-editable by patient and creator doctor.</p></div></div>
                  <div className="phf-trust-tile"><span className="phf-trust-ico phf-ico-green"><KeyRound size={20} /></span><div><h4>Access this file</h4><p>Share securely with any doctor by granting access.</p></div></div>
                </div>

                <footer className="phf-brandfoot" data-testid="phf-brand-footer">
                  <img src="/docsite-logo.png" alt="DocSite" />
                  <span>Powered by <b>DocSite</b> — Patient-owned Digital Health Files</span>
                </footer>
              </div>

              {/* ── BOOKING DRAWER COLUMN ─────────────────────────────── */}
              {drawerOpen && (
                <aside className="phf-drawer" data-testid="phf-drawer">
                  <div className="phf-drawer-head">
                    <CalendarDays size={20} className="phf-cal" />
                    <h2>Book Follow-up</h2>
                    <button className="phf-drawer-x" onClick={() => setDrawerOpen(false)} data-testid="phf-drawer-close"><X size={20} /></button>
                  </div>
                  <p className="phf-drawer-intro">Attach this digital health file to your appointment so the doctor can review it before you arrive.</p>

                  <div className="phf-step">
                    <span className="phf-step-num">1</span>
                    <div className="phf-step-body">
                      <div className="phf-step-title">Choose doctor for your follow-up</div>
                      <button type="button" onClick={() => setBookWith('current')} className={`phf-radio-card ${bookWith === 'current' ? 'phf-radio-card--active' : ''}`} data-testid="phf-doctor-current">
                        <span className={`phf-radio ${bookWith === 'current' ? 'phf-radio--on' : ''}`} />
                        <span className="phf-radio-ico"><UserRoundCheck size={17} /></span>
                        <div><div className="phf-radio-title">Current doctor - {d.doctor?.name || 'your doctor'}</div><div className="phf-radio-sub">Full file available to this doctor as creator doctor.</div></div>
                      </button>
                      <button type="button" onClick={() => setBookWith('new')} className={`phf-radio-card ${bookWith === 'new' ? 'phf-radio-card--active' : ''}`} data-testid="phf-doctor-new">
                        <span className={`phf-radio ${bookWith === 'new' ? 'phf-radio--on' : ''}`} />
                        <span className="phf-radio-ico"><UserPlus size={17} /></span>
                        <div><div className="phf-radio-title">Book with a new doctor</div><div className="phf-radio-sub">This file will be shared only after your consent.</div></div>
                      </button>
                    </div>
                  </div>

                  <div className="phf-step">
                    <span className="phf-step-num">2</span>
                    <div className="phf-step-body">
                      <div className="phf-step-title">File to attach</div>
                      <div className="phf-attach">
                        <div className="phf-attach-thumb"><i /><i /><i /><i /><i /><i /><i /></div>
                        <div>
                          <div className="phf-attach-label">Attached:</div>
                          <div className="phf-attach-name">DocSite Verified - Patient Digital Health File</div>
                          <span className="phf-attach-pill"><CheckCircle2 size={13} /> Complete clinical file</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="phf-step">
                    <span className="phf-step-num">3</span>
                    <div className="phf-step-body">
                      <div className="phf-step-title">Your consent</div>
                      <div className="phf-consent-note"><LockKeyhole size={15} /> Patient consent required before sharing with any other doctor.</div>
                      <div className="phf-consent-row">
                        <span className="phf-consent-check"><ShieldCheck size={16} /></span>
                        <span className="phf-consent-text">I consent to share this file for this appointment</span>
                        <button type="button" onClick={() => setConsent(c => !c)} className={`phf-toggle ${consent ? 'phf-toggle--on' : ''}`} data-testid="phf-consent-toggle" />
                      </div>
                      <div className="phf-consent-after"><FileText size={15} /> Selected doctor receives this file before the visit.</div>
                    </div>
                  </div>

                  <div className="phf-step">
                    <span className="phf-step-num">4</span>
                    <div className="phf-step-body">
                      <div className="phf-step-title">Pick a slot &amp; confirm</div>
                      <BookFollowUpBooking
                        slug={doctorSlug}
                        healthFileToken={healthFileToken}
                        doctorName={d.doctor?.name}
                        consent={consent}
                        disabled={bookWith !== 'current' || !doctorSlug}
                      />
                    </div>
                  </div>

                  <p className="phf-drawer-foot">DocSite Turbo — your file travels with the booking.</p>
                </aside>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Tile({ icon, ico, title, children, cls = '', testid }) {
  return (
    <div className={`phf-tile ${cls}`} data-testid={testid}>
      <div className="phf-tile-head"><span className={`phf-tile-ico ${ico}`}>{icon}</span><h3>{title}</h3></div>
      {children}
    </div>
  );
}

/* Clamp long clinical text to N lines with a View more / View less toggle so
   a verbose doctor note can never blow up a card. */
function ClampText({ text, lines = 6, pre }) {
  const [open, setOpen] = useState(false);
  const [overflow, setOverflow] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (el) setOverflow(el.scrollHeight - el.clientHeight > 4);
  }, [text, lines]);
  const clampStyle = open ? {} : { display: '-webkit-box', WebkitLineClamp: lines, WebkitBoxOrient: 'vertical', overflow: 'hidden' };
  return (
    <>
      <p ref={ref} className={`phf-tile-p ${pre ? 'phf-clamp-pre' : ''}`} style={clampStyle}>{text}</p>
      {(overflow || open) && <button className="phf-viewmore" onClick={() => setOpen(o => !o)}>{open ? 'View less' : 'View more'}</button>}
    </>
  );
}
